import { User } from "src/types/user";
import { ADGroup } from "src/types/adGroup";
import { Feature, FEATURES } from "src/utils/feature";
import _ from "lodash";
import { GetPartnersResponse } from "src/types/apiResponse";
export class AuthenticatedUser implements User {
	// Prefixes of the raw AD-group strings that grant access to each portal.
	// Matched case-insensitively, mirroring how roles are parsed on the backend.
	private static readonly ANALYTICS_ROLE_PREFIX = "ANALYTICS_";
	private static readonly SALES_PRACTICE_ROLE_PREFIX = "PP_SPC_";
	private static readonly REDPEN_ROLE_PREFIX = "PP_REDPEN_";

	// Exact (case-insensitive) role names that grant access to the Solutions tab —
	// one per partner the portal supports (plain and "PP_"-prefixed forms), plus
	// CAPITALONE for internal users.
	private static readonly SOLUTIONS_ROLES = new Set([
		"BJS",
		"CLUB",
		"KOHLS",
		"MENARDS",
		"NMG",
		"REI",
		"WSI",
		"T_MOBILE",
		"DISCOVER",
		"AURUS",
		"CAPITALONE",
		"PP_BJS",
		"PP_CLUB",
		"PP_KOHLS",
		"PP_MENARDS",
		"PP_NMG",
		"PP_REI",
		"PP_WSI",
		"PP_T_MOBILE",
		"PP_DISCOVER",
		"PP_AURUS",
		"PP_CAPITALONE",
	]);

	// Exact (case-insensitive) role names that grant access to the Certifications tab.
	private static readonly CERTIFICATIONS_ROLES = new Set([
		"CAPITALONE",
		"PARTNER_PORTAL_ADMIN",
		"PP_CAPITALONE",
		"PP_CERTIFICATIONS_ADMIN",
	]);

	userId: string;
	firstName: string;
	lastName: string;
	phone: string | null;
	email: string;
	isExternalUser: boolean;

	features: Feature[];
	partners: string[];
	roles: string[];

	partnerAccess: GetPartnersResponse;

	commonId: string | null;

	constructor(user: User, partnerAccess: GetPartnersResponse | null) {
		this.userId = user.userId;
		this.firstName = user.firstName;
		this.lastName = user.lastName;
		this.phone = user.phone;
		this.email = user.email;
		this.isExternalUser = user.isExternalUser;
		this.commonId = user.commonId;
		this.features = user.features;
		this.partners = user.partners;
		this.roles = user.roles ?? [];
		if (partnerAccess != null) {
			this.partnerAccess = partnerAccess;
		}
	}

	private hasRoleWithPrefix(prefix: string): boolean {
		return this.roles.some((role) => role.toUpperCase().startsWith(prefix));
	}

	private hasRoleInSet(roleSet: Set<string>): boolean {
		return this.roles.some((role) => roleSet.has(role.toUpperCase()));
	}

	hasAnalyticsRole(): boolean {
		return this.hasRoleWithPrefix(AuthenticatedUser.ANALYTICS_ROLE_PREFIX);
	}

	hasSalesPracticeRole(): boolean {
		return this.hasRoleWithPrefix(AuthenticatedUser.SALES_PRACTICE_ROLE_PREFIX);
	}

	hasSolutionsRole(): boolean {
		return this.hasRoleInSet(AuthenticatedUser.SOLUTIONS_ROLES);
	}

	hasCertificationsRole(): boolean {
		return this.hasRoleInSet(AuthenticatedUser.CERTIFICATIONS_ROLES);
	}

	hasRedpenRole(): boolean {
		return this.hasRoleWithPrefix(AuthenticatedUser.REDPEN_ROLE_PREFIX);
	}

	isInternalAdmin(): boolean {
		return this.features.includes(FEATURES.INTERNAL_ADMIN);
	}

	hasAccessToFeature(targetFeatures: Feature[]): boolean {
		if (this.isInternalAdmin()) return true;

		for (let i = 0; i < targetFeatures.length; i++) {
			if (this.features.includes(targetFeatures[i])) return true;
		}

		return false;
	}

	hasAccessToAtLeastOneOfTargetFeatures(targetFeatures: Feature[][]): boolean {
		for (let i = 0; i < targetFeatures.length; i++) {
			if (this.hasAccessToFeature(targetFeatures[i])) return true;
		}

		return false;
	}

	hasHasAccessToAccessTypeAndPartnerIfRequired(
		requiredAccessTypeCode: string | undefined,
		requiredPartnerCode: string | undefined
	): boolean {
		if (this.isInternalAdmin()) return true;

		if (!requiredAccessTypeCode && !requiredPartnerCode) return true;

		if (requiredAccessTypeCode && !requiredPartnerCode)
			return this.hasAccessToAccessType(requiredAccessTypeCode);

		if (!requiredAccessTypeCode && requiredPartnerCode)
			return this.hasAccessToPartner(requiredPartnerCode);

		if (requiredAccessTypeCode && requiredPartnerCode)
			return this.hasAccessToAccessTypeAndPartner(
				requiredAccessTypeCode,
				requiredPartnerCode
			);

		return false;
	}

	private hasAccessToAccessTypeAndPartner(
		accessTypeCode: string,
		partnerCode: string
	) {
		return (
			this.isInternalAdmin() ||
			(this.hasAccessToAccessType(accessTypeCode) &&
				this.hasAccessToPartner(partnerCode))
		);
	}

	private hasAccessToAccessType(accessTypeCode: string): boolean {
		return (
			this.isInternalAdmin() ||
			!(accessTypeCode === "INT" && this.isExternalUser)
		);
	}

	private hasAccessToPartner(partnerCode: string): boolean {
		const ALL_PARTNERS_ACCESS_CODE = "ALL";

		return (
			this.partners.includes(partnerCode) ||
			this.partners.includes(ALL_PARTNERS_ACCESS_CODE) ||
			this.isInternalAdmin()
		);
	}
}
